import {
  DataRequest,
  dataRequestTypeLabels,
  DataRequestWithDate,
  isDataRequestWithDate,
} from "@/ai/autotext";
import { DatePicker } from "@/components-v2/DatePicker";
import { useTranslation } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { toLocalDateISOString } from "@/utils/Dates";
import { stopPropagation } from "@/utils/events";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $addUpdateTag,
  $createNodeSelection,
  $getNodeByKey,
  $getSelection,
  $setSelection,
  LexicalEditor,
  NodeKey,
  SKIP_DOM_SELECTION_TAG,
} from "lexical";
import { forwardRef, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

export type DCVariableComponentProps<T extends DataRequest = DataRequest> = {
  dataRequest: T;
  nodeKey: NodeKey;
  onDataRequestChange: (dataRequest: T) => void;
};

const isNodeSelected = (editor: LexicalEditor, nodeKey: NodeKey) =>
  editor.getEditorState().read(() => {
    const node = $getNodeByKey(nodeKey);
    return node?.isSelected() && $getSelection()?.getNodes().length === 1;
  });

export const DCVariableComponent = ({
  dataRequest,
  nodeKey,
  onDataRequestChange,
}: DCVariableComponentProps) => {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setIsSelected] = useState(() => isNodeSelected(editor, nodeKey));
  const setSelected = useCallback(() => {
    if (!isSelected) {
      editor.update(() => {
        const selection = $createNodeSelection();
        selection.add(nodeKey);
        $setSelection(selection);
      });
    }
  }, [editor, nodeKey, isSelected]);

  useEffect(() => {
    let isMounted = true;
    const unregister = editor.registerUpdateListener(() => {
      if (isMounted) {
        setIsSelected(isNodeSelected(editor, nodeKey));
      }
    });
    return () => {
      isMounted = false;
      unregister();
    };
  }, [editor, nodeKey]);

  if (isDataRequestWithDate(dataRequest)) {
    return (
      <DCVariableWithDateComponent
        dataRequest={dataRequest}
        nodeKey={nodeKey}
        onDataRequestChange={onDataRequestChange}
        isSelected={isSelected}
        setSelected={setSelected}
      />
    );
  } else {
    return (
      <DCVariableDefaultComponent
        dataRequest={dataRequest}
        nodeKey={nodeKey}
        onDataRequestChange={onDataRequestChange}
        isSelected={isSelected}
        setSelected={setSelected}
      />
    );
  }
};

const DCVariablePill = forwardRef<
  HTMLElement,
  PropsWithChildren<{
    dataRequest: DataRequest;
    nodeKey: NodeKey;
    isSelected?: boolean;
    setSelected: () => void;
  }>
>(({ dataRequest, isSelected, setSelected, children }, ref) => {
  const { t } = useTranslation();
  return (
    <small
      ref={ref}
      onClick={stopPropagation(setSelected)}
      data-tooltip={t(dataRequestTypeLabels[dataRequest.type])}
      className={classNames(
        "rounded-md",
        "color-primary-dark",
        "bg-primary-light",
        "inline-block",
        "select-none",
        "px-1",
        isSelected && "ring-3 ring-primary",
      )}
    >
      {children}
    </small>
  );
});

export const DCVariableDefaultComponent = (
  props: DCVariableComponentProps & {
    isSelected?: boolean;
    setSelected: () => void;
  },
) => {
  return <DCVariablePill {...props}>{props.dataRequest.type}</DCVariablePill>;
};

const DCVariableWithDateComponent = (
  props: DCVariableComponentProps<DataRequestWithDate> & {
    isSelected?: boolean;
    setSelected: () => void;
  },
) => {
  const { dataRequest, nodeKey, onDataRequestChange, isSelected } = props;
  const [editor] = useLexicalComposerContext();
  const popupRef = useRef<HTMLDivElement | null>(null);

  const closeDatePicker = useCallback(() => {
    const node = $getNodeByKey(nodeKey);
    const selection = node?.selectEnd();
    if (selection) {
      $setSelection(selection);
    }
  }, [nodeKey]);

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as HTMLElement)) {
        editor.update(() => {
          // prevent editor to re-focus itself when clicking outside the editor to close the datepicker
          $addUpdateTag(SKIP_DOM_SELECTION_TAG);
          closeDatePicker();
        });
      }
    };
    document.body.addEventListener("click", listener, true);
    return () => {
      document.body.removeEventListener("click", listener, true);
    };
  }, [closeDatePicker, editor]);

  return (
    <BasePopover.Root
      open={isSelected}
      onOpenChange={(open, event) => {
        if (!open) {
          switch (event.reason) {
            case "escape-key":
              editor.update(() => {
                closeDatePicker();
              });
              break;
          }
        }
      }}
    >
      <BasePopover.Trigger
        nativeButton={false}
        render={
          <DCVariablePill {...props}>
            <span>{dataRequest.type}</span>
            {dataRequest.perDate && <span>;{dataRequest.perDate}</span>}
          </DCVariablePill>
        }
      ></BasePopover.Trigger>
      <BasePopover.Portal>
        <BasePopover.Positioner sideOffset={5} align={"start"}>
          <BasePopover.Popup ref={popupRef}>
            <DatePicker
              input={dataRequest.perDate}
              onSelect={(date) => {
                editor.update(() => {
                  closeDatePicker();
                  onDataRequestChange({
                    ...dataRequest,
                    perDate: toLocalDateISOString(date),
                  });
                });
              }}
            />
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
};
